import axios, { InternalAxiosRequestConfig, AxiosError } from 'axios';

// Use relative URL to leverage the Nginx proxy
const API_URL = '';

// Configure axios
axios.defaults.baseURL = API_URL;

// Add a request interceptor
axios.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error: AxiosError) => Promise.reject(error)
);

// Add a response interceptor to handle unauthorized responses
axios.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    // If the error is not related to authorization, just reject as normal
    if (error.response?.status !== 401) {
      return Promise.reject(error);
    }

    // Don't redirect to login if we're already on the login page
    const isLoginRequest = error.config?.url === '/api/login';
    if (isLoginRequest) {
      return Promise.reject(error);
    }

    // Otherwise, this is a 401 error on a non-login request
    // Note: We don't automatically logout here, just let the component handle it
    return Promise.reject(error);
  }
);

// Event Types
export interface Event {
  id: string;
  external_event_id: string;
  status: string;
  created_at: string;
  updated_at: string;
}

export interface EventWithUser extends Event {
  user_name: string;
}

export interface Log {
  id: string;
  event_id: string;
  message: string;
  created_at: string;
}

export interface CreateEventRequest {
  external_event_id: string;
}

// User Types
export interface User {
  id: string;
  name: string;
}

export interface UpdateUserCredentials {
  external_user_id?: string;
  external_auth_token?: string;
}

// Auth API
export const login = async (username: string, password: string) => {
  const response = await axios.post('/api/login', { username, password });
  return response.data;
};

// User API
export const getCurrentUser = async (): Promise<User> => {
  const response = await axios.get('/api/me');
  return response.data;
};

export const updateUserCredentials = async (credentials: UpdateUserCredentials): Promise<User> => {
  const response = await axios.put('/api/me/credentials', credentials);
  return response.data;
};

// Events API
export const getEvents = async (): Promise<Event[]> => {
  const response = await axios.get('/api/events');
  return response.data;
};

export const getAllEvents = async (): Promise<EventWithUser[]> => {
  const response = await axios.get('/api/events/all');
  return response.data;
};

export const getEvent = async (eventId: string): Promise<Event> => {
  const response = await axios.get(`/api/events/${eventId}`);
  return response.data;
};

export const getEventLogs = async (eventId: string): Promise<Log[]> => {
  const response = await axios.get(`/api/events/${eventId}/logs`);
  return response.data;
};

export const createEvent = async (event: CreateEventRequest): Promise<Event> => {
  const response = await axios.post('/api/events', event);
  return response.data;
};

export const cancelEvent = async (eventId: string): Promise<Event> => {
  const response = await axios.delete(`/api/events/${eventId}`);
  return response.data;
}; 